'use client'

import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'

// Tracked commute places — the user's top navigation targets (design
// data.jsx:239-243). Distances to these are computed for real via Google
// Distance Matrix (see ./distance), addresses geocoded via the backend.

export type CommuteMode = 'transit' | 'walk' | 'bike' | 'car'

export interface CommutePlace {
  id: string
  label: string
  address: string
  /** Resolved via the backend geocoder; undefined until the address resolves. */
  lat?: number
  lon?: number
  mode: CommuteMode
}

// Top 3 navigation targets. The user can edit these; capped at MAX_PLACES.
export const MAX_PLACES = 3

// Empty by default — a fresh install / account has no tracked destinations, so
// no commute tiles show until the user adds one (in Settings or from a card).
// Added addresses resolve via the geocoder on first use.
export const DEFAULT_PLACES: CommutePlace[] = []

interface CommutePlacesState {
  places: CommutePlace[]
  setPlaces: (places: CommutePlace[]) => void
}

const STORAGE_KEY = 'roostr-commute-places'

export const useCommutePlaces = create<CommutePlacesState>()(
  persist(
    (set) => ({
      places: DEFAULT_PLACES,
      setPlaces: (places) => set({ places: places.slice(0, MAX_PLACES) }),
    }),
    {
      name: STORAGE_KEY,
      version: 1,
      storage: createJSONStorage(() => localStorage),
      // v0 (the mock) had up to 4 coordinate-less places — cap to 3; missing
      // coords resolve lazily via the geocoder on first use.
      migrate: (persisted: unknown) => {
        const state = persisted as Partial<CommutePlacesState> | undefined
        const places = Array.isArray(state?.places) ? state!.places.slice(0, MAX_PLACES) : DEFAULT_PLACES
        return { places } as CommutePlacesState
      },
    }
  )
)

// Cross-tab sync: 'storage' fires only in OTHER tabs when the persisted key
// changes, so rehydrating here keeps every open tab on the latest places
// instead of last-write-wins clobbering.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key === STORAGE_KEY) void useCommutePlaces.persist.rehydrate()
  })
}
